import { put, select, take, spawn, takeEvery, fork, cancel, takeLatest, all } from "redux-saga/effects";
import { trimStart } from "lodash";

import { history } from "../../router/MainRouter";
import { Routes } from "../..//constant/routes";
import ACTION from "../../store/action";
import { ApiNames } from "../../api/api";
import { callApi } from "./networkActions";
import {
  selectIsPhiAllowed,
  selectSite,
  selectFilters,
  selectPatients,
  selectCurrentPage,
  selectSearchedText,
  selectCurrentCycle,
  selectOrdersTableSearchedText,
  selectSorting,
  selectViewType,
  selectedSavedListFilters,
  selectedSavedListActiveColumn,
  selectPatientTableColumns,
  selectFilterList,
  selectSavedListViewType,
  selectCalendarOrderDateFilter,
  selectMultiples,
  selectAPSponsors,
  selectAssigneesAndUserGroups,
  selectCycleAdminDateFilter,
  selectPatientCycles,
  selectSelectedOrderId
} from "../selector";
import { getSkipLimit, SAVED_LIST_PAGE_SIZE, searchTypes, ViewType } from "../../constant/general";
import { getPatients } from "./index";
import UniversalCookie from "universal-cookie";
import { defaultMultiples, defaultSavedListFilters } from "../../constant/filters";
import SuccessMessage from "../../component/customComponent/customMessages/SuccessMessage";
import { parseSelectedColumns } from "../../utils/misc";
import { patientTableColumns } from "../../constant/patientTableColumns";
import ErrorMessage from "../../component/customComponent/customMessages/ErrorMessage";
import { defaultSort } from "../../utils/sort";
import { ERR_CONFLICT } from "../../constant/errors";
import { Statuses } from "../../constant/status";
import i18next from "i18next";
import { formatAssignee } from "../../utils/formaters";

const MB = 1000000;
const MAX_FILE_SIZE = 15 * MB;
const cookies = new UniversalCookie();

function* getGetCycles({ pharmacyPatient }) {
  const adminDateFilter = yield select(selectCycleAdminDateFilter);
  const getCyclesRes = yield callApi(ApiNames.GetCycles, pharmacyPatient, adminDateFilter);
  yield put(ACTION.setPatientCycles(getCyclesRes.allCycles, getCyclesRes.filteredCycles));
}

function* refreshCycles({ pharmacyPatient }) {
  yield getGetCycles({ pharmacyPatient });
}

function* getNotes({ patientId, pharmacyPatient }) {
  const isPhiAllowed = yield select(selectIsPhiAllowed);
  if (isPhiAllowed) {
    const notesRes = yield callApi(ApiNames.Notes, patientId);
    yield put(ACTION.setPatientNotes(pharmacyPatient, notesRes.notes));
  } else {
    ErrorMessage(`PHI is not allowed: Can't display notes`);
  }
}

function* getAttachments({ patientId, pharmacyPatient }) {
  const isPhiAllowed = yield select(selectIsPhiAllowed);
  if (isPhiAllowed) {
    const attachmentsRes = yield callApi(ApiNames.Attachments, patientId);
    yield put(ACTION.setCanViewOrDownloadPatientAttachmentsAction(attachmentsRes.canViewOrDownload));
    yield put(ACTION.setPatientAttachments(pharmacyPatient, attachmentsRes.data["phi-attachments"]));
  } else {
    ErrorMessage(`PHI is not allowed: Can't display attachments`);
  }
}

function* addPatientAction({ patient }) {
  const patientRes = yield callApi(ApiNames.PharmacyPatientsAdd, patient);
  if (patientRes && patientRes.success) {
    const site = yield select(selectSite);
    SuccessMessage(`Patient ${site}-${patientRes.patient.id} created - analyzing data in progress`);
    yield refreshOrders();
    yield spawn(patientAutomateAfterCreateAction, {
      patientId: patientRes.patient.id
    });
    history.push(Routes.ROOT);
  } else {
    if (patientRes && patientRes.errors) {
      ErrorMessage(`Patient creation failed: ${patientRes.errors[0].message}`);
    }
  }
}

function* patientAutomateAfterCreateAction({ patientId }) {
  try {
    const patientAutomateRes = yield callApi(ApiNames.PatientAutomateAfterCreate, patientId);

    if (patientAutomateRes && patientAutomateRes.success) {
      SuccessMessage(`Patient’s ${patientId} data was successfully analyzed and updated`);
    } else {
      ErrorMessage(`Could not analyze patient's ${patientId} data`);
    }
  } catch (error) {
    ErrorMessage(`Could not analyze patient's ${patientId} data`);
  }
}

function* setPatientAction({ pharmacyPatient, patientId, orderId }) {
  yield put(ACTION.setIdPatientModal(orderId));
  yield put(ACTION.setTracking(null));

  const isPhiAllowed = yield select(selectIsPhiAllowed);
  if (!isPhiAllowed) {
    const patients = yield select(selectPatients);
    const currentOrderIdIndex = patients.findIndex((p) => p.currentOrder.id === orderId);
    yield put(ACTION.setCurrentCycle(patients[currentOrderIdIndex]));
  }
  const getGetCyclesTask = yield fork(getGetCycles, { pharmacyPatient });
  const getNotesTask = yield fork(getNotes, { patientId, pharmacyPatient });
  const getAttachmentsTask = yield fork(getAttachments, {
    patientId,
    pharmacyPatient
  });
  const getTasksTask = yield fork(getTasks, { patientId });
  yield take(ACTION.Types.CLEAR_PATIENT);
  yield cancel([getGetCyclesTask, getNotesTask, getAttachmentsTask, getTasksTask]);
}

function* editCurrentOrderAction({ currentOrder, pharmacyPatient }) {
  yield put(ACTION.setIsLoadingEnabled(true));
  const editPatientCycleRes = yield callApi(ApiNames.EditPatientCycle, currentOrder.id, currentOrder);
  const newCurrentOrder = editPatientCycleRes["patient-orders"][0];
  yield put(ACTION.editCurrentOrder(newCurrentOrder));
  yield getGetCycles({ pharmacyPatient });
  const currentCycle = yield select(selectCurrentCycle);
  if (currentCycle) yield getPatientOrderAction({ pharmacyPatient: pharmacyPatient });
  yield refreshPatientsTable();
  yield put(ACTION.setIsLoadingEnabled(false));
}

function* setNextCycleAction({ pharmacyPatient, currentOrderIndex }) {
  yield put(ACTION.setIsLoadingEnabled(true));
  const nextCycleRes = yield callApi(ApiNames.NextCycle, pharmacyPatient, currentOrderIndex);
  if (nextCycleRes && !nextCycleRes.errors) {
    yield getGetCycles({ pharmacyPatient: pharmacyPatient });
    const currentCycle = yield select(selectCurrentCycle);
    if (currentCycle) yield getPatientOrderAction({ pharmacyPatient: pharmacyPatient }); // no need to refresh current cycle if not in current cycle tab
    yield refreshPatientsTable();

    // Auto-switch to next order if the marked-as-done order is currently selected
    const selectedOrderId = yield select(selectSelectedOrderId);
    const cycles = yield select(selectPatientCycles);

    if (cycles && cycles.length > 0) {
      // Find the order that was just marked as done
      const doneOrder = cycles.find((cycle) => cycle.orderIndex === currentOrderIndex);
      // Check if the done order matches the currently selected order
      if (doneOrder && selectedOrderId === doneOrder.id) {
        // Find the next order (orderIndex + 1)
        const nextOrder = cycles.find((cycle) => cycle.orderIndex === currentOrderIndex + 1);
        if (nextOrder) {
          yield put(ACTION.setSelectedOrder(nextOrder.id));
        }
      }
    }
  } else {
    ErrorMessage(`Next order failed`);
  }
  yield put(ACTION.setIsLoadingEnabled(false));
}

function* refreshPatientsTable(noPagination) {
  const searchText = yield select(selectSearchedText);
  if (searchText && searchText.length > 0) {
    const searchBy = cookies.get("searchBy");
    yield searchPatientAction({ page: 1, text: searchText, searchBy });
  } else {
    yield refreshOrders(noPagination);
  }
}

function* changeCurrentOrderAction({ pharmacyPatient, currentOrder }) {
  yield callApi(ApiNames.BulkChange, pharmacyPatient, currentOrder.id);
  yield getPatientOrderAction({ pharmacyPatient: pharmacyPatient.id });
  yield refreshPatientsTable();
}

function* switchOrderAction({ orderId }) {
  // This is UI-only switching to view/edit different orders
  yield put(ACTION.setSelectedOrder(orderId));
}

function* setBulkChangeAction({ pharmacyPatient, orders, stopEditingCycleMode, noPagination }) {
  const currentCycle = yield select(selectCurrentCycle);
  const bulkChangeRes = yield callApi(ApiNames.BulkChange, pharmacyPatient, null, orders);
  if (bulkChangeRes) {
    if (currentCycle) {
      const currentCycleIndex = bulkChangeRes.orders.findIndex((order) => order.id === currentCycle.currentOrder.id);
      const updatedCurrentOrder = bulkChangeRes.orders[currentCycleIndex];
      yield put(ACTION.setCurrentCycle({ ...currentCycle, currentOrder: updatedCurrentOrder }));
    } else {
      const updatedPatientOrder = bulkChangeRes.orders[0];
      yield put(ACTION.editCurrentOrder(updatedPatientOrder));
    }

    const patientId = bulkChangeRes.pharmacyPatient.patient;
    yield all(
      [
        fork(getGetCycles, { pharmacyPatient: pharmacyPatient.id }),
        fork(refreshPatientsTable, noPagination),
        currentCycle ? fork(getPatientOrderAction, { pharmacyPatient: bulkChangeRes.pharmacyPatient.id }) : null,
        fork(getAttachments, { patientId, pharmacyPatient: pharmacyPatient.id }),
        fork(getNotes, { patientId, pharmacyPatient: pharmacyPatient.id })
      ].filter(Boolean)
    );

    SuccessMessage("Order updated successfully");
  } else {
    yield getGetCycles({ pharmacyPatient: pharmacyPatient.id });
  }

  if (stopEditingCycleMode) yield put(ACTION.setKeepEditingCycleMode(false));
}

function* addPatientCycleAction({ newCycle, setNextCycle }) {
  yield put(ACTION.setIsLoadingEnabled(true));
  const patientOrdersRes = yield callApi(ApiNames.PatientOrders, newCycle);
  if (!patientOrdersRes || patientOrdersRes?.err) {
    if (patientOrdersRes?.err === ERR_CONFLICT) {
      ErrorMessage("This order was already added, please refresh your page");
    } else {
      ErrorMessage("Failed to create order");
    }
    return;
  }

  if (patientOrdersRes && patientOrdersRes["patient-orders"][0]) {
    yield put(ACTION.addPatientCycle(patientOrdersRes["patient-orders"][0]));
    if (!setNextCycle) {
      yield getPatientOrderAction({ pharmacyPatient: patientOrdersRes["patient-orders"][0].pharmacyPatient });
      yield refreshPatientsTable();

      const newOrderId = patientOrdersRes["patient-orders"][0].id;
      if (newOrderId) {
        yield put(ACTION.setSelectedOrder(newOrderId));
      }
    }
  } else {
    ErrorMessage("Failed to create order");
  }

  if (setNextCycle) {
    yield setNextCycleAction({
      pharmacyPatient: setNextCycle.pharmacyPatient,
      currentOrderIndex: setNextCycle.orderIndex
    });
  }
  SuccessMessage(`Order was added successfully`);
  yield getGetCycles({ pharmacyPatient: patientOrdersRes["patient-orders"][0].pharmacyPatient });
  yield put(ACTION.setIsLoadingEnabled(false));
  yield put(ACTION.setIsAddingPatientCycle(false));
}

function* deletePatientCycleAction({ id, pharmacyPatient }) {
  const res = yield callApi(ApiNames.CycleDelete, id);

  if (res.success) {
    yield getGetCycles({ pharmacyPatient: pharmacyPatient.id });
    yield callApi(ApiNames.PharmacyPatients, {
      id: pharmacyPatient.id,
      numberOfCycles: pharmacyPatient.numberOfCycles - 1
    });
    yield put(ACTION.setIdPatientModal(pharmacyPatient.pharmacyPatientCurrentOrder));
    yield getPatientOrderAction({ pharmacyPatient: pharmacyPatient.id });
    SuccessMessage(`Order deleted from patient successfully`);
    yield refreshOrders();
    yield getAttachments({ patientId: pharmacyPatient.patient.id, pharmacyPatient: pharmacyPatient.id });
    yield getNotes({ patientId: pharmacyPatient.patient.id, pharmacyPatient: pharmacyPatient.id });
  } else {
    ErrorMessage(`Failed to delete the current order. Please try again or reload the page.`);
  }
}

function* setPharmacyPatientAction({ pharmacyPatient }) {
  yield callApi(ApiNames.PharmacyPatients, pharmacyPatient);
  const currentCycle = yield select(selectCurrentCycle);
  if (currentCycle) yield getPatientOrderAction({ pharmacyPatient: pharmacyPatient.id });
  yield refreshOrders();
}

function* addPatientNoteAction({ patientId, note, orderIndex, pharmacyPatient }) {
  const notesAddRes = yield callApi(ApiNames.NotesAdd, patientId, note, orderIndex, pharmacyPatient);
  if (notesAddRes.success) {
    yield getNotes({ patientId, pharmacyPatient });
    yield getPatientOrderAction({ pharmacyPatient: pharmacyPatient });
    SuccessMessage("Note added successfully");
  } else {
    ErrorMessage(`Note added failed: ${notesAddRes.errors[0].message}`);
  }
}
function* editPatientNoteAction({ noteId, note, patientId, pharmacyPatient }) {
  const notesEditRes = yield callApi(ApiNames.NotesEdit, { noteId, note });
  if (notesEditRes.note) {
    yield getNotes({ patientId, pharmacyPatient });
    SuccessMessage("Note successfully updated");
  } else {
    ErrorMessage(`Note edit failed: ${notesEditRes.errors?.[0]?.message}`);
  }
}

function* addPatientAttachmentAction({ patientId, pharmacyPatient, file, orderIndex }) {
  if (file.size > MAX_FILE_SIZE) {
    ErrorMessage(`File size extands ${MAX_FILE_SIZE / MB} MB max: ${(file.size / MB).toFixed(2)} MB`);
  } else {
    const attachmentsUploadRes = yield callApi(
      ApiNames.AttachmentsUpload,
      patientId,
      pharmacyPatient,
      file,
      orderIndex
    );
    if (attachmentsUploadRes && attachmentsUploadRes.status) {
      yield getAttachments({ patientId, pharmacyPatient });
      yield getPatientOrderAction({ pharmacyPatient: pharmacyPatient });
      SuccessMessage("File uploaded successfully");
    } else {
      ErrorMessage(`File upload failed: ${attachmentsUploadRes.errors[0].message}`);
    }
  }
}

function* deletePatientNoteAction({ patientId, noteId, pharmacyPatient }) {
  const notesDeleteRes = yield callApi(ApiNames.NoteDelete, patientId, noteId, pharmacyPatient);
  if (notesDeleteRes) {
    yield put(ACTION.deletePatientNote(noteId));
    yield getNotes({ patientId, pharmacyPatient });
    yield getPatientOrderAction({ pharmacyPatient: pharmacyPatient });
    SuccessMessage("Note successfully deleted");
  } else {
    ErrorMessage(`Note delete failed`);
  }
}

function* deletePatientAttachmentsAction({ id, attachmentUniqueId, pharmacyPatient }) {
  const attachmentsDeleteRes = yield callApi(ApiNames.AttachmentsDelete, id, attachmentUniqueId, pharmacyPatient);
  if (attachmentsDeleteRes.success) {
    yield put(ACTION.deletePatientAttachments(id));
    SuccessMessage("File deleted successfully");
    yield getPatientOrderAction({ pharmacyPatient: pharmacyPatient });
  } else {
    ErrorMessage(`File delete failed: ${attachmentsDeleteRes.errors[0].message}`);
  }
}

function* getTrackingAction({ carrier, trackingNumber }) {
  const trackingRes = yield callApi(ApiNames.Tracking, carrier, trackingNumber);
  if (trackingRes.success) {
    yield put(ACTION.setTracking(trackingRes.trackingInfo));
  } else {
    ErrorMessage(`Tracking package failed: ${trackingRes.errors ? trackingRes.errors[0].message : ""}`);
    yield put(ACTION.setTracking(null));
  }
}

function* getTasks({ patientId }) {
  const isPhiAllowed = yield select(selectIsPhiAllowed);
  if (isPhiAllowed) {
    const tasksRes = yield callApi(ApiNames.Tasks, patientId);

    yield put(ACTION.setPatientTasks(tasksRes.todos));
  } else {
    ErrorMessage(`PHI is not allowed: Can't display tasks`);
  }
}

function* addPatientTaskAction({ task, pharmacyPatient }) {
  const tasksRes = yield callApi(ApiNames.TasksAdd, task, pharmacyPatient);
  if (tasksRes?.todos) {
    yield put(ACTION.addPatientTasks(tasksRes.todos));
    yield getPatientOrderAction({ pharmacyPatient: pharmacyPatient });
    SuccessMessage("Task added successfully");
  } else {
    ErrorMessage(`Task add failed: ${tasksRes?.errors[0]?.message}`);
  }
}

function* editPatientTaskAction({ task, pharmacyPatient }) {
  const tasksEditRes = yield callApi(ApiNames.TasksEdit, task, pharmacyPatient);
  if (tasksEditRes.todos) {
    yield put(ACTION.updatePatientTasks(tasksEditRes.todos));
    yield getPatientOrderAction({ pharmacyPatient: pharmacyPatient });
    SuccessMessage("Task edited successfully");
  } else {
    ErrorMessage(`Task edit failed: ${tasksEditRes.errors[0].message}`);
  }
}

function* deletePatientTaskAction({ task, pharmacyPatient }) {
  const tasksDeleteRes = yield callApi(ApiNames.TasksDelete, task, pharmacyPatient);
  if (tasksDeleteRes.todos) {
    yield put(ACTION.deletePatientTasks(tasksDeleteRes.todos));
    yield getPatientOrderAction({ pharmacyPatient: pharmacyPatient });
    SuccessMessage("Task deleted successfully");
  } else {
    ErrorMessage(`Task delete failed: ${tasksDeleteRes.errors[0].message}`);
  }
}

function* sendSupportTicket({ subject, comment, patient, user }) {
  const SupportRes = yield callApi(ApiNames.Support, subject, comment, patient, user);
  if (SupportRes) {
    yield put(ACTION.setShowPatientModal(false));
    SuccessMessage("Ticket send successfully");
  } else {
    ErrorMessage("Ticket send failed");
  }
}

function* setFiltersAction({ filters, apiName, page = 1, sorting }) {
  const {
    showInactiveFilter,
    orderDateFilter,
    adminDateFilter,
    estimatedDeliveryDateFilter,
    receivedDateFilter,
    billVoidedDateFilter,
    assigneesFilter,
    facilitiesFilter,
    manufacturersFilter,
    physiciansFilter,
    statusesFilter,
    orderTypesFilter,
    billVoidedFilter,
    drugsFilter,
    drugDoseFormCategoriesFilter,
    orderViewFilter,
    forceCallApi = false,
    isUnassigned
  } = filters;

  const currFilters = yield select(selectFilters);
  const searchText = yield select(selectOrdersTableSearchedText);
  const viewType = yield select(selectViewType);
  const calendarOrderDateFilter = yield select(selectCalendarOrderDateFilter);
  const savedListViewType = yield select(selectSavedListViewType);
  const noPagination = viewType === ViewType.Calendar.key || savedListViewType === ViewType.Calendar.key ? true : false;
  const orderDate =
    viewType === ViewType.Calendar.key || savedListViewType === ViewType.Calendar.key
      ? calendarOrderDateFilter
      : orderDateFilter;
  if (
    ((forceCallApi ||
      currFilters.showInactiveFilter !== showInactiveFilter ||
      currFilters.orderDateFilter !== orderDateFilter ||
      currFilters.adminDateFilter !== adminDateFilter ||
      currFilters.estimatedDeliveryDateFilter !== estimatedDeliveryDateFilter ||
      currFilters.receivedDateFilter !== receivedDateFilter ||
      currFilters.billVoidedDateFilter !== billVoidedDateFilter ||
      currFilters.assigneesFilter !== assigneesFilter ||
      currFilters.facilitiesFilter !== facilitiesFilter ||
      currFilters.manufacturersFilter !== manufacturersFilter ||
      currFilters.physiciansFilter !== physiciansFilter ||
      currFilters.statusesFilter !== statusesFilter ||
      currFilters.orderTypesFilter !== orderTypesFilter ||
      currFilters.billVoidedFilter !== billVoidedFilter ||
      currFilters.drugsFilter !== drugsFilter ||
      currFilters.drugDoseFormCategoriesFilter !== drugDoseFormCategoriesFilter ||
      currFilters.orderViewFilter !== orderViewFilter ||
      currFilters.isUnassigned !== isUnassigned) &&
      (!searchText || searchText.length === 0)) ||
    (noPagination && searchText.length)
  ) {
    yield getPatients(
      showInactiveFilter,
      orderDate,
      adminDateFilter,
      estimatedDeliveryDateFilter,
      receivedDateFilter,
      billVoidedDateFilter,
      assigneesFilter,
      facilitiesFilter,
      manufacturersFilter,
      physiciansFilter,
      statusesFilter,
      orderTypesFilter,
      billVoidedFilter,
      drugsFilter,
      drugDoseFormCategoriesFilter,
      orderViewFilter,
      isUnassigned,
      page,
      apiName,
      sorting,
      noPagination
    );
  } else {
    yield searchPatientTextFiltersAction({
      page,
      text: searchText,
      searchBy: searchTypes.name_id.key,
      filters: { ...filters, orderDateFilter: orderDate },
      sorting,
      noPagination
    });
  }
}

function* searchPatientAction({ page, text = "", searchBy }) {
  if (text.trim() === "") {
    yield put(ACTION.setSearchedPatients([]));
    return;
  }
  const sorting = yield select(selectSorting);
  let searchRes = yield callApi(ApiNames.SearchFreeText, page, trimStart(text), searchBy, sorting);
  if (searchRes) {
    // Add sorting so we can properly sort in the reducer
    searchRes = { ...searchRes, sorting };
    yield put(ACTION.setSearchedPatients(searchRes));
  }
}

function* searchPatientTextFiltersAction({ page, text = "", searchBy, filters, noPagination }) {
  const { skip, limit } = getSkipLimit(page);
  const sorting = yield select(selectSorting);

  const {
    showInactiveFilter,
    orderDateFilter,
    adminDateFilter,
    estimatedDeliveryDateFilter,
    receivedDateFilter,
    billVoidedDateFilter,
    assigneesFilter,
    facilitiesFilter,
    manufacturersFilter,
    physiciansFilter,
    statusesFilter,
    orderTypesFilter,
    billVoidedFilter,
    drugsFilter,
    drugDoseFormCategoriesFilter,
    orderViewFilter,
    isUnassigned
  } = filters;
  const searchRes = yield callApi(
    ApiNames.SearchByTextAndFilters,
    trimStart(text),
    searchBy,
    showInactiveFilter,
    orderDateFilter,
    adminDateFilter,
    estimatedDeliveryDateFilter,
    receivedDateFilter,
    billVoidedDateFilter,
    assigneesFilter,
    facilitiesFilter,
    manufacturersFilter,
    physiciansFilter,
    statusesFilter,
    orderTypesFilter,
    billVoidedFilter,
    drugsFilter,
    drugDoseFormCategoriesFilter,
    orderViewFilter,
    isUnassigned,
    skip,
    limit,
    sorting,
    noPagination
  );
  if (searchRes) {
    yield put(ACTION.setPatients(searchRes.patients));
    yield put(ACTION.setTotalPatients(searchRes.total));
  }
}

function* clonePharmacyPatientAction({ pharmacyPatientId }) {
  const cloneRes = yield callApi(ApiNames.Clone, pharmacyPatientId);
  if (cloneRes && cloneRes.success) {
    const site = yield select(selectSite);
    SuccessMessage(`Patient ${site}-${cloneRes.pharmacyPatient.patient} was cloned successfully`);
    yield refreshPatientsTable();
  } else {
    ErrorMessage("Pharmacy patient clone failed");
  }
}

function* setUserEmailConfiguration({ emailConfiguration: { triggerName, active } }) {
  const userEmailConfigurationRes = yield callApi(ApiNames.EmailConfiguration, { triggerName, active });

  if (userEmailConfigurationRes) {
    yield put(ACTION.setUserEmailConfiguration(userEmailConfigurationRes));
  } else {
    ErrorMessage("Couldn't edit email configuration");
  }
}

function* updateNotificationPreferences({ preferenceType, targetId, notificationType, isEnabled }) {
  const updateRes = yield callApi(
    ApiNames.UpdateNotificationPreferences,
    preferenceType,
    targetId,
    notificationType,
    isEnabled
  );
  if (!updateRes) {
    ErrorMessage("Failed to update notification preferences");
  }
  const notificationPreferencesRes = yield callApi(ApiNames.NotificationPreferences);
  if (notificationPreferencesRes) {
    yield put(ACTION.setNotificationPreferences(notificationPreferencesRes));
  } else {
    ErrorMessage("Failed to load notification preferences");
  }
}

function* refreshOrders(noPagination) {
  const page = yield select(selectCurrentPage);
  const filters = yield select(selectFilters);
  const sorting = yield select(selectSorting);
  filters.forceCallApi = true;
  yield put(ACTION.setFiltersAction(filters, ApiNames.SearchWithoutRefresh, page, sorting, noPagination));
}

function* getPatientOrderAction({ pharmacyPatient }) {
  const isPhiAllowed = yield select(selectIsPhiAllowed);
  if (isPhiAllowed) {
    const searchRes = yield callApi(ApiNames.PatientPhiInfo, pharmacyPatient);
    if (searchRes) {
      searchRes.pharmacyPatientCurrentOrder = searchRes.currentOrder.id;
      yield put(ACTION.setCurrentCycle(searchRes));
    }
  } else {
    ErrorMessage(`PHI is not allowed: Can't display patient PHI info`);
  }
}

function* getPatientOrderByIdAction({ orderId }) {
  const isPhiAllowed = yield select(selectIsPhiAllowed);
  if (!isPhiAllowed) {
    const searchRes = yield callApi(ApiNames.SearchOrder, orderId);
    if (searchRes) {
      return searchRes;
    }
  } else {
    ErrorMessage(`PHI is not allowed: Can't display patient PHI info`);
  }
}

function* createFilterList(data) {
  try {
    const createSavedListRes = yield callApi(ApiNames.CreateFilterList, data);
    if (createSavedListRes) {
      SuccessMessage(`'${createSavedListRes.name}' list was saved`, {
        closeButtonId: "saved_list_view_list_closed",
        title: "View List",
        redirectTo: `${Routes.ROOT}${Routes.SAVEDLISTS}/${createSavedListRes.id}/${ViewType.List.key}`,
        linkId: "saved_list_view_list"
      });
      yield put(ACTION.setFormPopups(false));
    }
  } catch (err) {
    const errMessage = err?.response?.data?.errors ? err.response.data.errors[0].message : err.message;
    ErrorMessage(errMessage);
  }
}

function* searchSavedList({ page }) {
  const { query, filterBy } = yield select(selectedSavedListFilters);
  const { name: orderBy, sortBy } = yield select(selectedSavedListActiveColumn);
  const skip = Math.abs((page - 1) * SAVED_LIST_PAGE_SIZE);
  const filters = {
    ...defaultSavedListFilters,
    query,
    filterBy,
    skip,
    orderBy,
    sortBy
  };

  const savedListSearchRes = yield callApi(ApiNames.searchSavedList, filters);

  if (savedListSearchRes && page > 1 && savedListSearchRes.data?.length === 0) {
    yield put(ACTION.setSavedListSelectedPage(page - 1));
    yield put(ACTION.searchSavedListAction(page - 1));
  } else {
    yield put(ACTION.setTableData(savedListSearchRes, page));
  }
}

function* updateFilterList({ data, filterListId }) {
  try {
    const updateSavedListRes = yield callApi(ApiNames.UpdateFilterList, data, filterListId);
    if (updateSavedListRes) {
      SuccessMessage(`List was successfully updated `);
      // Ensure the API response includes the params we just saved
      // This prevents the saved params from being overwritten by the API response
      const updatedFilterList = {
        ...updateSavedListRes,
        params: data.params || updateSavedListRes.params
      };
      yield put(ACTION.setFilterList(updatedFilterList));
      yield put(ACTION.searchSavedListAction(1));
      yield put(ACTION.setFormPopups(false));
    }
  } catch (err) {
    const errMessage = err?.response?.data?.errors ? err.response.data.errors[0].message : err.message;
    ErrorMessage(errMessage);
  }
}

function* applySavedListFilters(filters, { sorter }) {
  yield put(ACTION.setFiltersAction(filters, ApiNames.SearchWithoutRefresh, 1, sorter, false));
}

function* setMultiples(filters) {
  const multiples = yield select(selectMultiples);
  const apSponsors = yield select(selectAPSponsors);
  const assignees = yield select(selectAssigneesAndUserGroups);

  const selectedStatuses = Object.values(Statuses)
    .filter((option) => filters.statusesFilter.includes(option.key))
    .map((option) => ({ value: option.key, text: option.title(i18next.t.bind(i18next)) }));

  // Physician tooltip is handled in the component (OrdersFilter) via allKnownPhysicians ref
  const selectedPhysicians = (filters.physiciansFilter || []).map((id) => ({ value: id, text: "" }));

  // Facility tooltip is handled in the component (OrdersFilter) via allKnownFacilities ref
  const selectedFacilities = (filters.facilitiesFilter || []).map((id) => ({ value: id, text: "" }));

  const selectedManufacturers = apSponsors
    .filter((option) => filters.manufacturersFilter.includes(option))
    .map((option) => ({ value: option, text: option }));

  const selectedAssignees = Object.keys(assignees)
    .filter((option) => filters.assigneesFilter.includes(option))
    .map((option) => ({ value: option, text: formatAssignee(assignees[option]) }));

  yield put(
    ACTION.setMultiplesFilter({
      ...multiples,
      assigneesFilter: selectedAssignees,
      statusesFilter: selectedStatuses,
      physiciansFilter: selectedPhysicians,
      facilitiesFilter: selectedFacilities,
      manufacturersFilter: selectedManufacturers
    })
  );
}

function* findSavedListById(data) {
  const savedListSearchRes = yield callApi(ApiNames.FindSavedListById, data.data);
  if (savedListSearchRes && savedListSearchRes.isValid) {
    const paramsRes = JSON.parse(savedListSearchRes?.params);
    savedListSearchRes.params = paramsRes;
    yield put(ACTION.setCurrentPage(1));
    yield all([
      put(ACTION.setFilterList(savedListSearchRes)),
      put(ACTION.setFilters(savedListSearchRes?.params?.filters)),
      put(ACTION.setSorting(savedListSearchRes.params?.sorting))
    ]);
    yield put(ACTION.initSavedListAction());
    yield setMultiples(savedListSearchRes?.params?.filters);
    // Note: merge columns
    const newTableColumns = JSON.parse(JSON.stringify(patientTableColumns));
    paramsRes?.selectedColumns && parseSelectedColumns(newTableColumns, paramsRes.selectedColumns);
    yield put(ACTION.setPatientTableColumns(newTableColumns));

    yield applySavedListFilters(
      { ...paramsRes.filters, forceCallApi: true },
      { sorter: savedListSearchRes.params.sorting }
    );
  } else {
    ErrorMessage("This saved list could not be loaded - please try again or select another list.");
    history.push(`${Routes.ROOT}${Routes.SAVEDLISTS}`);
  }
}

function* clearSavedListRelatedDataAction() {
  const patientTableColumnsList = yield select(selectPatientTableColumns);
  const filterList = yield select(selectFilterList);
  yield put(ACTION.setPatientTableColumns(filterList.id ? patientTableColumns : patientTableColumnsList));
  yield put(ACTION.setFilterList({}));
  yield put(ACTION.setSorting(defaultSort));
  yield put(ACTION.setSavedListViewType(ViewType.List.key));
  yield put(ACTION.setOrdersTableSearchText(""));
  yield put(ACTION.setMultiplesFilter(defaultMultiples));
}

function* getRecentPatientsAction({ selectedPage, onlyFirstRecentPatients }) {
  const recentPatientsRes = yield callApi(ApiNames.GetRecentPatients, selectedPage);

  if (selectedPage === 1) {
    yield put(ACTION.setFirstRecentPatients({ ...recentPatientsRes }, selectedPage));
  }
  if (!onlyFirstRecentPatients) {
    yield put(ACTION.setRecentPatientsAction(recentPatientsRes, selectedPage));
  }
}

function* actions() {
  yield takeEvery(ACTION.Types.ADD_PATIENT_ACTION, addPatientAction);
  yield takeEvery(ACTION.Types.PATIENT_AUTOMATE_AFTER_CREATE_ACTION, patientAutomateAfterCreateAction);
  yield takeEvery(ACTION.Types.SET_PATIENT_ACTION, setPatientAction);
  yield takeEvery(ACTION.Types.EDIT_CURRENT_ORDER_ACTION, editCurrentOrderAction);
  yield takeEvery(ACTION.Types.SET_NEXT_CYCLE_ACTION, setNextCycleAction);
  yield takeEvery(ACTION.Types.DELETE_PATIENT_CYCLE_ACTION, deletePatientCycleAction);
  yield takeEvery(ACTION.Types.CHANGE_CURRENT_ORDER_ACTION, changeCurrentOrderAction);
  yield takeEvery(ACTION.Types.SWITCH_ORDER_ACTION, switchOrderAction);
  yield takeEvery(ACTION.Types.BULK_CHANGE_ACTION, setBulkChangeAction);
  yield takeEvery(ACTION.Types.ADD_PATIENT_CYCLE_ACTION, addPatientCycleAction);
  yield takeEvery(ACTION.Types.SET_PHARMACY_PATIENT_ACTION, setPharmacyPatientAction);
  yield takeEvery(ACTION.Types.ADD_PATIENT_NOTE_ACTION, addPatientNoteAction);
  yield takeEvery(ACTION.Types.EDIT_PATIENT_NOTE_ACTION, editPatientNoteAction);
  yield takeEvery(ACTION.Types.DELETE_PATIENT_NOTE_ACTION, deletePatientNoteAction);
  yield takeEvery(ACTION.Types.ADD_PATIENT_ATTACHMENT_ACTION, addPatientAttachmentAction);
  yield takeEvery(ACTION.Types.DELETE_PATIENT_ATTACHMENT_ACTION, deletePatientAttachmentsAction);
  yield takeEvery(ACTION.Types.ADD_PATIENT_TASK_ACTION, addPatientTaskAction);
  yield takeEvery(ACTION.Types.EDIT_PATIENT_TASK_ACTION, editPatientTaskAction);
  yield takeEvery(ACTION.Types.DELETE_PATIENT_TASK_ACTION, deletePatientTaskAction);
  yield takeEvery(ACTION.Types.GET_TRACKING_ACTION, getTrackingAction);
  yield takeEvery(ACTION.Types.SEND_SUPPORT_TICKET, sendSupportTicket);
  yield takeEvery(ACTION.Types.SET_FILTERS_ACTION, setFiltersAction);
  yield takeEvery(ACTION.Types.SEARCH_PATIENTS_ACTION, searchPatientAction);
  yield takeEvery(ACTION.Types.SEARCH_PATIENTS_TEXT_FILTERS_ACTION, searchPatientTextFiltersAction);
  yield takeEvery(ACTION.Types.CLONE_PHARMACY_PATIENT_ACTION, clonePharmacyPatientAction);
  yield takeEvery(ACTION.Types.SET_USER_EMAIL_CONFIGURATION_ACTION, setUserEmailConfiguration);
  yield takeEvery(ACTION.Types.UPDATE_NOTIFICATION_PREFERENCES_ACTION, updateNotificationPreferences);
  yield takeEvery(ACTION.Types.GET_PATIENT_ORDER, getPatientOrderAction);
  yield takeEvery(ACTION.Types.GET_PATIENT_ORDER_BY_ID, getPatientOrderByIdAction);
  yield takeEvery(ACTION.Types.CREATE_FILTER_LIST_ACTION, createFilterList);
  yield takeLatest(ACTION.Types.SEARCH_SAVED_LIST_ACTION, searchSavedList);
  yield takeEvery(ACTION.Types.UPDATE_FILTER_LIST_ACTION, updateFilterList);
  yield takeLatest(ACTION.Types.SEARCH_SAVED_LIST_BY_ID, findSavedListById);
  yield takeEvery(ACTION.Types.CLEAR_SAVED_LIST_RELATED_DATA_ACTION, clearSavedListRelatedDataAction);
  yield takeEvery(ACTION.Types.GET_RECENT_PATIENTS, getRecentPatientsAction);
  yield takeEvery(ACTION.Types.REFRESH_CYCLES_ACTION, refreshCycles);
}

export { actions, getRecentPatientsAction };
